'use client'

import { ContentFile } from "@/lib/types/file-types"
import { isUploadingFile, hasUploadError, getFilePreviewUrl } from "@/lib/types/file-types"
import { formatFileSize } from "@/lib/utils"
import { AlertTriangle, Loader2, X, Play } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { motion } from "motion/react"
import Image from "next/image"
import { useState, useEffect, useMemo } from "react"
import {
    File,
    FileImage,
    FileText,
    FileVideo,
    FileAudio,
    FileCode,
    Archive,
    Table,
    Presentation
} from "lucide-react"
import { AnimatePresence } from "motion/react"
import { cn } from "@/lib/utils"
import { toast } from "sonner"


export function FileCard({
    file,
    onDelete,
    onRemove,  // Legacy prop
    allowDelete = true,
    showRemove = true,  // Legacy prop  
    validationError
}: {
    file: ContentFile
    onDelete?: (file: ContentFile) => void
    onRemove?: (fileId: string) => void  // Legacy prop name for backward compatibility
    allowDelete?: boolean
    showRemove?: boolean  // Legacy prop name for backward compatibility
    validationError?: string
}) {
    // Simplified state checking using helper functions
    const uploading = isUploadingFile(file)
    const hasError = hasUploadError(file) || !!validationError
    const progress = file.uploadState?.progress || 0
    const errorMessage = validationError || file.uploadState?.error || null

    // Truncate filename if too long
    const truncatedName = file.name.length > 35
        ? (() => {
            const lastDotIndex = file.name.lastIndexOf('.')
            const extension = lastDotIndex !== -1 ? file.name.substring(lastDotIndex) : ''
            const nameWithoutExt = lastDotIndex !== -1 ? file.name.substring(0, lastDotIndex) : file.name
            return nameWithoutExt.slice(0, 25) + '...' + extension
        })()
        : file.name

    const handleDelete = () => {
        if (onDelete) {
            onDelete(file)
        } else if (onRemove) {
            onRemove(file.id)
        }
    }

    const shouldShowRemove = (allowDelete || showRemove) && (onDelete || onRemove)

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
                opacity: 0,
                y: -10,
                transition: { duration: 0.1, ease: "easeOut" }
            }}
            transition={{ duration: 0.2 }}
            className="relative border rounded-lg h-14 bg-card flex items-center px-2 py-2"
        >
            <div className="flex items-center space-x-3 flex-1 min-w-0">
                {/* <FileIcon
                    file={file}
                    size={40}
                /> */}
                <RingAndFadeAnimation file={file} />
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate text-foreground">
                        {truncatedName}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{formatFileSize(file.size)}</span>
                        {hasError && (
                            <span className="text-destructive font-medium flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {errorMessage}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {shouldShowRemove && (
                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 flex-shrink-0"
                    onClick={handleDelete}
                >
                    <X className="h-3 w-3" />
                </Button>
            )}

            {/* Progress bar for uploading files - positioned at bottom */}
            {/* Commented out in favor of circular progress animation in FileIcon
            {uploading && (
                <div className="absolute bottom-0 left-0 right-0">
                    <Progress 
                        value={progress} 
                        className="h-1 w-full rounded-none rounded-b-lg"
                    />
                </div>
            )}
            */}
        </motion.div>
    )
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// HELPERS FUNCTIONS 
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function RingAndFadeAnimation({
    file,
    iconSize = 40
}: {
    file: ContentFile
    iconSize?: number
}) {
    console.log('file', file.uploadState?.progress)
    const [isFinalContentLoaded, setIsFinalContentLoaded] = useState(false)
    const [preservedObjectUrl, setPreservedObjectUrl] = useState<string | null>(null)
    const [videoThumbnail, setVideoThumbnail] = useState<string | null>(null)
    
    // A file is complete if it's done uploading OR if it's already uploaded (has URL but no uploadState)
    const isComplete = file.uploadState?.status === 'complete' || (!file.uploadState && !!file.url)
    const progress = file.uploadState?.progress || 0

    const radius = (iconSize - 4) / 2
    const circumference = 2 * Math.PI * radius
    const offset = circumference - (Math.min(progress, 100) / 100) * circumference

    const isImage = file.mediaCategory === 'image'
    const isVideo = file.mediaCategory === 'video'

    // Create and preserve object URL for images during upload
    useEffect(() => {
        if (file.uploadState?.file && !preservedObjectUrl && isImage) {
            const objectUrl = URL.createObjectURL(file.uploadState.file)
            setPreservedObjectUrl(objectUrl)
        }
    }, [file.uploadState?.file, preservedObjectUrl, isImage])

    // Extract video thumbnail when upload completes or for already uploaded videos
    useEffect(() => {
        if (!isVideo || !file.url || !isComplete || videoThumbnail) return

        const extractFrame = async () => {
            try {
                const video = document.createElement('video')
                video.crossOrigin = 'anonymous'
                video.currentTime = 1 // 1 second in

                await new Promise((resolve, reject) => {
                    video.onloadeddata = resolve
                    video.onerror = reject
                    video.src = file.url!
                })

                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')

                canvas.width = iconSize
                canvas.height = iconSize

                if (ctx) {
                    ctx.drawImage(video, 0, 0, iconSize, iconSize)
                    const frameDataUrl = canvas.toDataURL('image/jpeg', 0.8)
                    setVideoThumbnail(frameDataUrl)
                    setIsFinalContentLoaded(true)
                }
            } catch (err) {
                console.error('Error extracting video frame:', err)
                setIsFinalContentLoaded(true) // Still mark as loaded to remove progress
            }
        }

        extractFrame()
    }, [isVideo, file.url, isComplete, videoThumbnail, iconSize])

    // For non-media files, mark as loaded when upload completes or for already uploaded files
    useEffect(() => {
        if (!isImage && !isVideo && isComplete) {
            setIsFinalContentLoaded(true)
        }
    }, [isImage, isVideo, isComplete])

    // For already uploaded images (from database), mark as loaded immediately
    useEffect(() => {
        if (isImage && isComplete && !file.uploadState && file.url) {
            setIsFinalContentLoaded(true)
        }
    }, [isImage, isComplete, file.uploadState, file.url])

    // Clean up object URL when component unmounts or final content loads
    useEffect(() => {
        return () => {
            if (preservedObjectUrl) {
                URL.revokeObjectURL(preservedObjectUrl)
            }
        }
    }, [preservedObjectUrl])

    // Clean up object URL when final content is fully loaded
    useEffect(() => {
        if (isFinalContentLoaded && preservedObjectUrl) {
            setTimeout(() => {
                URL.revokeObjectURL(preservedObjectUrl)
                setPreservedObjectUrl(null)
            }, 200) // Small delay to ensure smooth transition
        }
    }, [isFinalContentLoaded, preservedObjectUrl])

    // Get appropriate icon for non-media files
    const getFileIcon = () => {
        const extension = file.name.split('.').pop()?.toLowerCase() || ''
        const iconProps = { size: iconSize * 0.6, className: "flex-shrink-0 " }

        switch (file.mediaCategory) {
            case 'audio':
                return <FileAudio {...iconProps} className="text-emerald-500" />
            case 'document':
                return <FileText {...iconProps} className="text-blue-500" />
            case 'spreadsheet':
                return <Table {...iconProps} className="text-green-500" />
            case 'presentation':
                return <Presentation {...iconProps} className="text-amber-500" />
            default:
                // Handle by file extension
                if (['js', 'ts', 'tsx', 'jsx', 'py', 'java', 'cpp', 'c', 'html', 'css'].includes(extension)) {
                    return <FileCode {...iconProps} className="text-cyan-500" />
                }
                if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
                    return <Archive {...iconProps} className="text-orange-500" />
                }
                return <File {...iconProps} className="text-muted-foreground" />
        }
    }

    return (
        <motion.div
            className="relative flex-shrink-0 flex items-center justify-center"
            style={{ width: iconSize, height: iconSize }}
            layout
        >
            {/* Content container - different behavior based on file type */}
            <motion.div
                className={cn(
                    "absolute inset-0 overflow-hidden bg-transparent flex items-center justify-center",
                    (isComplete && isFinalContentLoaded) && "border-2 border-background16"
                )}
                animate={{
                    borderRadius: (isComplete && isFinalContentLoaded) ? "6px" : "50%",
                    scale: (isComplete && isFinalContentLoaded) ? 1 : 0.93,
                }}
                transition={{ duration: 0.4, ease: "circOut" }}
            >
                {/* Image handling */}
                {isImage && (
                    <>
                        {/* Object URL preview */}
                        {preservedObjectUrl && (
                            <motion.div
                                className="absolute inset-0 z-20"
                                animate={{
                                    opacity: isFinalContentLoaded ? 0 : 1
                                }}
                                transition={{ duration: 0.2 }}
                            >
                                <Image
                                    key={preservedObjectUrl}
                                    src={preservedObjectUrl}
                                    alt={file.name}
                                    fill
                                    className="object-cover"
                                    unoptimized
                                    onError={() => {
                                        console.error('Failed to load object URL preview:', preservedObjectUrl)
                                    }}
                                />
                            </motion.div>
                        )}

                        {/* Final image from server */}
                        {file.url && (
                            <div className="absolute inset-0 z-10">
                                <Image
                                    key={file.url}
                                    src={file.url}
                                    alt={file.name}
                                    fill
                                    className="object-cover"
                                    unoptimized
                                    style={{
                                        opacity: isFinalContentLoaded ? 1 : 0
                                    }}
                                    onLoad={() => {
                                        if (isComplete) {
                                            setIsFinalContentLoaded(true)
                                        }
                                    }}
                                    onError={() => {
                                        console.error('Failed to load final image:', file.url)
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}

                {/* Video handling */}
                {isVideo && (
                    <>
                        {videoThumbnail ? (
                            <div className="absolute inset-0 z-10">
                                <img
                                    src={videoThumbnail}
                                    alt={file.name}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                    <Play size={iconSize * 0.3} className="text-white" />
                                </div>
                            </div>
                        ) : (
                            <FileVideo size={iconSize * 0.6} className="text-purple-500" />
                        )}
                    </>
                )}

                {/* Other file types - show appropriate icon */}
                {!isImage && !isVideo && getFileIcon()}
            </motion.div>

            {/* Progress ring - shows during upload and until final content loads */}
            <AnimatePresence>
                {(!isComplete || !isFinalContentLoaded) && (
                    <motion.div
                        key="progress-overlay"
                        className="absolute inset-0 pointer-events-none z-30"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { duration: 0.3 } }}
                    >
                        <svg className="absolute inset-0" width={iconSize} height={iconSize} style={{ width: iconSize, height: iconSize }}>
                            <circle
                                cx={iconSize / 2}
                                cy={iconSize / 2}
                                r={radius}
                                strokeWidth="2"
                                fill="none"
                                stroke="hsl(var(--border))"
                                opacity="0.3"
                            />
                        </svg>
                        
                        {/* Progress circle */}
                        <svg className="absolute inset-0" width={iconSize} height={iconSize} style={{ width: iconSize, height: iconSize }}>
                            <motion.circle
                                cx={iconSize / 2}
                                cy={iconSize / 2}
                                r={radius}
                                strokeWidth="2"
                                fill="none"
                                stroke="hsl(var(--primary))"
                                strokeDasharray={circumference}
                                strokeDashoffset={offset}
                                strokeLinecap="round"
                                transform={`rotate(-90 ${iconSize / 2} ${iconSize / 2})`}
                                animate={progress >= 100 ? {
                                    opacity: [1, 0.3, 1],
                                } : {
                                    opacity: 1
                                }}
                                transition={progress >= 100 ? {
                                    duration: 1,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                } : {
                                    duration: 0.5,
                                    ease: "linear"
                                }}
                            />
                        </svg>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}


// Video thumbnail component with first frame extraction
function VideoThumbnail({ file, size }: { file: ContentFile; size: number }) {
    const [thumbnail, setThumbnail] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    useEffect(() => {
        if (!file.url) {
            setLoading(false)
            return
        }

        const extractFrame = async () => {
            try {
                const video = document.createElement('video')
                video.crossOrigin = 'anonymous'
                video.currentTime = 1 // 1 second in

                await new Promise((resolve, reject) => {
                    video.onloadeddata = resolve
                    video.onerror = reject
                    video.src = file.url!
                })

                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')

                canvas.width = size
                canvas.height = size

                if (ctx) {
                    ctx.drawImage(video, 0, 0, size, size)
                    const frameDataUrl = canvas.toDataURL('image/jpeg', 0.8)
                    setThumbnail(frameDataUrl)
                }

                setLoading(false)
            } catch (err) {
                console.error('Error extracting video frame:', err)
                setError(true)
                setLoading(false)
            }
        }

        extractFrame()
    }, [file.url, size])

    if (loading) {
        return <FileVideo size={size * 0.6} className="text-purple-500 animate-pulse flex-shrink-0" />
    }

    if (error || !thumbnail) {
        return <FileVideo size={size * 0.6} className="text-purple-500 flex-shrink-0" />
    }

    return (
        <div
            className="relative rounded-md overflow-hidden flex-shrink-0 bg-muted"
            style={{ width: `${size}px`, height: `${size}px` }}
        >
            <img
                src={thumbnail}
                alt={file.name}
                className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <Play size={size * 0.3} className="text-white" />
            </div>
        </div>
    )
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Reusable image-only card for large previews
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function ImageOnlyCard({
    file,
    temporaryFile,
    className,
    imageClassName,
    heightClass = 'h-64'
}: {
    file: ContentFile
    temporaryFile?: ContentFile
    className?: string
    imageClassName?: string
    heightClass?: string
}) {
    const [isMainImageLoaded, setIsMainImageLoaded] = useState(false)
    const [isTempImageLoaded, setIsTempImageLoaded] = useState(false)

    const hasFinalImage = !!file && file.mediaCategory === 'image' && !!file.url
    const hasTempImage = !!temporaryFile && temporaryFile.mediaCategory === 'image' && !!temporaryFile.url
    const useTempAsMain = !hasFinalImage && hasTempImage

    if (!hasFinalImage && !hasTempImage) return null

    

    return (
        <div className={cn('relative inline-flex items-center justify-center bg-black border border-border rounded-lg p-2', className)}>
            {!isMainImageLoaded && !isTempImageLoaded && (
                <div className="absolute inset-0 bg-black flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
            )}

            {hasTempImage && !useTempAsMain && (
                <img
                    src={temporaryFile!.url}
                    alt={temporaryFile!.name}
                    className={cn(
                        'absolute w-auto rounded-md border border-border transition-opacity duration-300',
                        heightClass,
                        imageClassName,
                        isMainImageLoaded ? 'opacity-0' : 'opacity-100'
                    )}
                    onLoad={() => setIsTempImageLoaded(true)}
                />
            )}
            <img
                src={useTempAsMain ? temporaryFile!.url! : file!.url!}
                alt={useTempAsMain ? temporaryFile!.name : file!.name}
                className={cn('w-auto rounded-md border border-border', heightClass, imageClassName)}
                onLoad={() => {
                    if (useTempAsMain) setIsTempImageLoaded(true)
                    else setIsMainImageLoaded(true)
                }}
            />
        </div>
    )
}